<template>
  <div class="selst-container">
    <div class="white titleBar" id="titleBar">
      <CustomRow>
        <div class="py-3">
          <div
            class="text-small text-sm-page-title titleBanner font-weight-bold"
          >
            {{ session.name }}
          </div>
          <div
            v-if="selectedArea"
            class="text-small text-sm-page-title font-weight-regular"
          >
            {{ session.date }} {{ selectedArea.name }}
          </div>
          <v-spacer />
        </div>
      </CustomRow>
    </div>
    <CountDownTimer
      class="timer"
      :style="{ top: `${appBarHeight}px` }"
    ></CountDownTimer>
    <div class="order-content">
      <SeatsSelector
        :seats="seats"
        :info="ticketSeats"
        :limit="seatLimit"
        :overviewShow="isOverviewShow"
        @update="seatsUpdate"
        @updateOverview="updateOverview"
      />
    </div>
    <div class="order-footer">
      <CustomRow>
        <v-row class="py-3" :justify="'space-between'">
          <v-col cols="4" sm="3">
            <v-btn
              class="font-weight-bold"
              @click="preStep()"
              color="primary"
              block
              outlined
              style="border: 2px solid"
              depressed
              :x-large="!$vuetify.breakpoint.xs"
              >{{ $t("order_cancel") }}
            </v-btn>
          </v-col>
          <v-col cols="8" sm="6">
            <v-row>
              <v-col cols="6" class="py-0">
                <v-btn
                  class="font-weight-bold px-1"
                  color="primary"
                  outlined
                  style="border: 2px solid"
                  @click="toggleOverview()"
                  depressed
                  block
                  :x-large="!$vuetify.breakpoint.xs"
                  >{{
                    isOverviewShow
                      ? $t("sel_st_close_select_result")
                      : $t("sel_st_view_select_result")
                  }}</v-btn
                >
              </v-col>
              <v-col cols="6" class="py-0">
                <v-btn
                  class="nextBtn"
                  :elevation="0"
                  @click="confirmSeat()"
                  depressed
                  block
                  :class="{
                    disabledBtn: this.ticketSeats.length < seatLimit,
                  }"
                  :disabled="this.ticketSeats.length < seatLimit"
                  :x-large="!$vuetify.breakpoint.xs"
                  >{{ $t("layout_notification_confirm") }}</v-btn
                >
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </CustomRow>
    </div>
  </div>
</template>

<script>
import CountDownTimer from "./components/CountDownTimer";
import SeatsSelector from "./components/SeatsSelector";
import OrderTemplate from "./OrderTemplate";
import { getSessions } from "@/api/static";
import { mapGetters, mapActions } from "vuex";
import TM from "@/api/ticket-management";
import { CurrentReservedMixins } from "@/shared/mixins";
import RotateHandler from "@/shared/rotateHandler";
import { encrypt } from "@/vendor/crypto";

export default {
  mixins: [CurrentReservedMixins],
  components: {
    CountDownTimer,
    OrderTemplate,
    SeatsSelector,
  },
  data() {
    return {
      showTitleBanner: false,
      titleBarHeight: 0,
      appBarHeight: 0,
      session: {},
      seats: null,
      reserveTickets: [],
      ticketSeats: [],
      seats: [],
      products: [],
      seatLimit: 0,
      isOverviewShow: true,
    };
  },
  metaInfo() {
    return {
      title: `${this.$t("order_seat_step_2")} | ${this.$t("app_name")}`,
      script: [
        {
          type: "application/ld+json",
          json: {
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: `${this.$t("order_seat_step_2")} | ${this.$t("app_name")}`,
            url: window.location.href,
          },
        },
      ],
    };
  },
  watch: {
    isPopState: {
      deep: true,
      handler: function (newVal, old) {
        if (newVal !== old && !newVal) {
          this.init();
          this.setPopState(false);
        }
      },
    },
    session: {
      deep: true,
      handler(newVal) {
        if (newVal.name) {
          this.$nextTick(() => {
            this.titleBarHeight =
              document.getElementById("titleBar").clientHeight;
          });
        }
      },
    },
  },
  computed: {
    ...mapGetters({
      selectedArea: "order/selectedArea",
      tickets: "order/boughtTickets",
      isPopState: "order/isPopState",
      reserveResponse: "order/reserveResponse",
      campaign: "event/campaign",
      sessionInfo: "session/sessionInfo",
      countdown: "order/timer",
    }),
  },
  async mounted() {
    if (!this.isPopState) {
      this.init();
    }
  },
  methods: {
    ...mapActions({
      setCountDownTimer: "order/countDownTimer",
      showDialog: "dialog/show",
      updateOrder: "order/updateOrder",
      resetOrderState: "order/resetOrderState",
    }),
    updateOverview(bool) {
      this.isOverviewShow = bool;
    },
    toggleOverview(show) {
      this.isOverviewShow = !this.isOverviewShow;
    },
    async init() {
      const { hasReservedOrder } = await this.checkReservedOrder();
      if (!this.reserveResponse.orderId) {
        if (hasReservedOrder) await this.setReservedData();
        else
          this.$router
            .push({
              name: "ConfirmSt",
              params: {
                eventId: this.$route.params.eventId,
                sessionId: this.$route.params.sessionId,
              },
            })
            .catch(() => {});
      }

      const { data } = await getSessions(this.$route.params.eventId);
      const session = data.sessions.find(({ sessionId }) => {
        let sid = /^s[0-9]{9}$/.test(sessionId)
          ? encrypt(sessionId)
          : sessionId;
        return sid == this.$route.params.sessionId;
      });
      this.session = session;

      this.appBarHeight = document.getElementById("appBar").clientHeight;
      let products = [];

      if (!this.reserveResponse.tickets) {
        products = this.reserveResponse.products.reduce((accr, p) => {
          if (p.seats) {
            let seats = p.seats.map((s) => {
              return {
                ...p,
                ...s,
              };
            });
            accr = accr.concat(seats);
          } else {
            for (let i = 0; i < p.count; i++) {
              accr.push({
                ...p,
              });
            }
          }
          return accr;
        }, []);
        this.reserveTicket({
          ...this.reserveResponse,
          products: products,
          tickets: products,
        });
        this.reserveTickets = products;
      }
      this.showTitleBanner = false;
      this.ticketSeats = this.reserveResponse.tickets || [];
      this.seatLimit = this.reserveResponse.tickets
        ? this.reserveResponse.tickets.length
        : products.length;

      const { data: seatsData } = await TM.getSeatsByTicketAreaId(
        this.selectedArea.id
      );
      let rowStart = false;
      let emptyIndex = [];

      let tempSeats = seatsData.seats.reduce((accr, row, index) => {
        if (rowStart && !row.length) {
          accr.push(row);
        }
        if (row.length) {
          rowStart = true;
          emptyIndex.push(row.findIndex((seat) => !!seat));
          accr.push(row);
        }
        return accr;
      }, []);
      emptyIndex.sort((a, b) => a - b);
      const seatArray = tempSeats.map((row) => {
        let result = row.splice(
          emptyIndex[0] < 0 ? 0 : emptyIndex[0],
          row.length
        );
        return result;
      });
      if (seatsData.rotate) {
        RotateHandler.setData(seatArray, seatsData.rotate);
        this.seats = RotateHandler.doRotate();
      } else {
        this.seats = seatArray;
      }
      this.$store.dispatch("setOverlayLoading", false);
      this.setCountDownTimer();
      this.$gtm.trackView(`選擇座位-${this.session.name}`, this.$route.path);
      if (this.campaign.event_id === "3c039f1310567a346c3750670ba76cab") {
        this._initGtag("G-V0F0KV8V2Y");
      }
    },
    async preStep() {
      if (this.reserveResponse.orderId) {
        this.$store.dispatch("setOverlayLoading", true);
        const { data } = await TM.ticketRelease({
          orderId: this.reserveResponse.orderId,
        });
        this.isReleased = true;
        await this.resetOrderState();
        // console.log(`${String.fromCodePoint(128049)} [TM] release`, data);
      }

      this.$router
        .push({
          name: "Order1",
          params: { sessionId: this.$route.params.sessionId },
        })
        .catch(() => {});
      this.$store.dispatch("setOverlayLoading", false);
    },
    seatsUpdate(seats) {
      this.ticketSeats = seats;
    },
    async confirmSeat() {
      this.$store.dispatch("setOverlayLoading", true);
      // 更新座位資料
      let seatsInfo = this.ticketSeats.map((s) => {
        return {
          row: s.row,
          area: s.area,
          column: s.column,
        };
      });
      const { data: updateResponse } = await TM.ticketUpdate({
        orderId: this.reserveResponse.orderId,
        seatsInfo: { seats: seatsInfo },
      });

      if (updateResponse.errCode == "00") {
        this.updateOrder(updateResponse);
        return this.$router
          .push({
            name: "ConfirmSt",
            params: { sessionId: this.$route.params.sessionId },
          })
          .catch(() => {});
      } else {
        this.showDialog({
          alert: true,
          content: `<div class="pa-6 text-center font-weight-bold">
          <img
            src="/image/ic-warning@3x.png"
            alt=""
            style="width:80px"
          />
          <div class="mt-5">${this.$t("sel_st_select_fail")}</div>
        </div>`,
        });

        setTimeout(() => {
          this.$store.dispatch("setOverlayLoading", false);
        }, 3000);
      }
    },
  },
  beforeRouteLeave(to, from, next) {
    if (
      !["ConfirmSt", "Order2"].includes(to.name) &&
      this.reserveResponse.orderId &&
      !this.isReleased &&
      this.timer > 0
    ) {
      next(false);
      this.leaveConfirmation(to);
      return false;
    }
    next();
  },
};
</script>

<style lang="scss" scoped>
.selst-container {
  height: 100%;
  display: flex;
  flex-direction: column;

  .order-content {
    flex: 1;
    flex-grow: 1;
    padding-bottom: 58px;

    @media screen and (max-width: 960px) {
      padding-bottom: 36px;
    }

    @media screen and (max-width: 600px) {
      padding-bottom: 0px;
    }
  }
}

::v-deep {
  .titleBar {
    text-align: center;
  }

  .order-footer {
    position: fixed;
    bottom: 0;
    width: 100vw;
    display: block;
    background: white;
    box-shadow: 0 -3px 10px 0 rgba(0, 0, 0, 0.15);
    z-index: 5;

    .row {
      margin-top: 0;
      margin-bottom: 0;
    }

    .row [class*="col"] {
      padding-top: 0;
      padding-bottom: 0;
    }
  }

  @media screen and (max-width: 960px) {
    .titleBar {
      font-size: 0.7rem;
      text-align: left;

      .text-header-title {
        font-size: 1rem;
      }
    }

    .order-footer {
      .row {
        margin-left: -6px;
        margin-right: -6px;

        [class*="col"] {
          padding-top: 0;
          padding-bottom: 0;
          padding-left: 6px;
          padding-right: 6px;
        }
      }
    }
  }

  @media screen and (max-width: 600px) {
    .v-btn {
      font-size: 0.7rem;
      height: 32px;
      line-height: 32px;
    }
  }
}

.order-footer .v-btn,
.order-footer .v-btn.v-btn--outlined:not(.v-btn--disabled) {
  padding-left: 12px;
  padding-right: 12px;
}
</style>
